// src/services/inventoryService.ts
import { fetchApi } from './api';
import { appendProductCatRequestParams, type CatFilterAllFlag, type ProductCatFilterField } from '@/utils/productCatFilters';

type ProductCatApiParams = Partial<
  Record<ProductCatFilterField | CatFilterAllFlag, string | string[] | boolean>
>;

type IntIdListParam = number | number[] | undefined;

type ApiQueryParams = Record<string, string | number | boolean | number[] | string[]>;

function appendIntIdListParam(
  target: ApiQueryParams,
  key: string,
  value: IntIdListParam,
) {
  if (value === undefined || value === null) return;
  if (Array.isArray(value)) {
    const arr = value.filter((n) => typeof n === 'number' && !Number.isNaN(n));
    if (arr.length > 0) {
      target[key] = arr;
    }
    return;
  }
  if (!Number.isNaN(value)) {
    target[key] = value;
  }
}

export interface InventoryValueHistory {
  date: string;
  value: number;
}

export interface InventorySimulation {
  date: string;
  stock: number;
  reorderPoint: number;
  purchaseOrder?: {
    orderNumber: string;
    stock: number;
  };
}

export interface InventoryMovementHistory {
  date: string;
  stock: number;
  movement_in: number;
  movement_out: number;
}

export interface InventoryKpi {
  inventoryValue: number;
  inventoryMonths: number;
  economicImpact: number;
  economicImpactTrend: number;
  serviceLevel: number;
  serviceLevelTrend: number;
}

export interface InventoryHealthStatus {
  status: string;
  status_id?: number | null;
  status_order?: number;
  percentage: number;
  count: number;
  value?: number;
}

export type RecommendationColorVariant = 'danger' | 'warning' | 'success' | 'neutral';

export interface ReorderPoint {
  id: number;
  code: string;
  name: string;
  stock: number;
  monthly_avg_sales: number;
  inventory_policy__reorder_point: number | null;
  inventory_policy__reorder_point_suggested: number;
  /** Persistido en política; la API de tabla puede no incluirlo (solo sugerido). */
  inventory_policy__safety_stock?: number | null;
  inventory_policy__safety_stock_suggested: number;
  inventory_policy__lead_time_days: number;
  inventory_policy__service_level_target: number;
  safety_stock_percentage: number;
  stock_status: string;
  stock_status_order?: number;
  stock_status_name?: string;
  lifecycle_stage?: string; // Opcional: ya no se usa pero se mantiene por compatibilidad
  lifecycle_stage_order?: number; // Opcional: ya no se usa pero se mantiene por compatibilidad
  coverage_days?: number; // Calculado en el backend
  last_supplier: string;
  supplier_cost?: number; // Precio del último proveedor
  supplier_purchase_frequency: number;
  /** Meses con venta neta > 0 en el año en curso (SalesHistory). */
  sales_months_current_year?: number;
  /** Meses con venta neta > 0 en todo el historial (SalesHistory). */
  sales_months_total?: number;
  /** Fecha de la primera venta con quantity > 0 (ISO YYYY-MM-DD). */
  first_sale_date?: string | null;
  /** Meses calendario usados para calcular monthly_avg_sales (ventana ajustada a antigüedad). */
  monthly_avg_sales_months_used?: number;
  recommended_action?: string | null;
  action_category_key?: string | null;
  action_category_color?: RecommendationColorVariant | null;
  recommendation_on_demand?: boolean | null;
  /** Desde InventoryRecommendation (ETL); null si no hay fila de recomendación. */
  stock_en_transito?: number | null;
  /**
   * Grupo de equivalencia (INV-EQV-001). Presente solo si hay >1 miembro.
   * `stock` de la fila sigue siendo el del SKU visible; el total de grupo es informativo.
   */
  equivalence?: {
    principal_product_id: number;
    principal_code: string | null;
    canonical_product_id: number;
    conversion_pending: boolean;
    members: Array<{
      product_id: number;
      code: string;
      name: string;
      is_principal: boolean;
      stock: number;
    }>;
    stock_group_total: number;
  } | null;
}

export interface RecommendationChartBar {
  category_key: string;
  sort_order: number;
  color_variant: RecommendationColorVariant;
  on_demand_count: number;
  stock_count: number;
  total_count: number;
  recommended_actions: string[];
}

export interface RecommendationChartResponse {
  bars: RecommendationChartBar[];
  total_products: number;
  calculated_at: string | null;
}

export interface PolicyCalculations {
  total_inventory_value: number;
  months_of_inventory: number;
  inventory_health: InventoryHealthStatus[];
  reorder_points: ReorderPoint[];
  pagination?: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  };
}

export interface FilterOption {
  value: string | number;
  label: string;
  /** Texto de catálogo (p. ej. estados de stock en admin). */
  description?: string;
  /** Orden del semáforo de stock (`StockStatus.order`); color de barra y fallback si falta nombre. */
  order?: number;
}

export interface PolicyFilters {
  families: FilterOption[];
  stockStatuses: FilterOption[];
  suppliers?: FilterOption[];
  cat_1?: FilterOption[];
  cat_2?: FilterOption[];
  cat_3?: FilterOption[];
  cat_4?: FilterOption[];
  cat_5?: FilterOption[];
  cat_6?: FilterOption[];
}

export interface UpdateReorderPointRequest {
  reorder_point: number | null;
  internal_safety_stock?: number | null;
}

// Nuevas interfaces para gestión histórica de políticas
export interface InventoryPolicy {
  id: number;
  product: number;
  product_name: string;
  product_code: string;
  reorder_point: number | null;
  reorder_point_suggested: number;
  safety_stock: number;
  safety_stock_suggested: number;
  internal_safety_stock: number;
  lead_time_days: number;
  service_level_target: number;
  created_at: string;
  updated_at: string;
}

export interface PolicyHistoryResponse {
  product: {
    id: number;
    code: string;
    name: string;
  };
  policies: InventoryPolicy[];
}

export interface CreatePolicyRequest {
  product: number;
  reorder_point: number;
  reorder_point_suggested?: number;
  safety_stock: number;
  safety_stock_suggested?: number;
  lead_time_days: number;
  service_level_target?: number;
}

// API functions for inventory policies
export const inventoryService = {
  // Get policy filters
  getPolicyFilters: async (params?: {
    warehouse_id?: number;
    family?: number | number[];
    include_products?: boolean;
    /** Debe coincidir con la organización del usuario autenticado (validación en backend). */
    organization_id?: number;
  } & ProductCatApiParams): Promise<PolicyFilters & { products?: FilterOption[] }> => {
    const cleanParams: ApiQueryParams = {};
    if (params) {
      if (params.organization_id !== undefined && params.organization_id !== null && !isNaN(params.organization_id)) {
        cleanParams.organization_id = params.organization_id;
      }
      if (params.warehouse_id !== undefined && params.warehouse_id !== null && !isNaN(params.warehouse_id)) {
        cleanParams.warehouse_id = params.warehouse_id;
      }
      appendIntIdListParam(cleanParams, 'family', params.family);
      appendProductCatRequestParams(cleanParams, params);
      if (params.include_products !== undefined) {
        cleanParams.include_products = params.include_products ? 'true' : 'false';
      }
    }
    
    return fetchApi<PolicyFilters & { products?: FilterOption[] }>('inventory/policy-filters/', {
      params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined
    });
  },
  
  // Get policy calculations
  getPolicyCalculations: async (filters?: {
    family?: number | number[];
    stock_status?: number | number[];
    product_id?: number;
    warehouse_id?: number;
    page?: number;
    page_size?: number;
    ordering?: string; // Campo de ordenamiento (ej: 'code', '-name', 'stock', etc.)
    for_export?: boolean; // Si es true, devuelve todos los datos completos sin paginación
    only_kpis?: boolean; // Si es true, solo calcula KPIs y salud, saltando la tabla
    skip_table?: boolean; // Alias para only_kpis
    /** Si true, el backend no recalcula KPIs si no hay caché (usar junto a only_kpis en otra petición previa). */
    skip_kpis?: boolean;
    /** Meses de ventas históricas para KPI "meses de inventario" (3, 6, 12, 18 o 24). Default 6. */
    sales_months?: number;
    /** Debe coincidir con la organización del usuario autenticado (validación en backend). */
    organization_id?: number;
    action_category_key?: string;
    recommendation_on_demand?: boolean;
    supplier_id?: number | number[];
    positive_stock_only?: boolean;
  } & ProductCatApiParams): Promise<PolicyCalculations> => {
    // Filtrar parámetros undefined/null para evitar enviar valores vacíos
    const cleanParams: ApiQueryParams = {};
    if (filters) {
      if (filters.organization_id !== undefined && filters.organization_id !== null && !isNaN(filters.organization_id)) {
        cleanParams.organization_id = filters.organization_id;
      }
      appendIntIdListParam(cleanParams, 'family', filters.family);
      appendProductCatRequestParams(cleanParams, filters);
      if (filters.stock_status !== undefined && filters.stock_status !== null) {
        if (Array.isArray(filters.stock_status)) {
          const arr = filters.stock_status.filter(
            (n) => typeof n === 'number' && !Number.isNaN(n)
          );
          if (arr.length > 0) {
            cleanParams.stock_status = arr;
          }
        } else if (!Number.isNaN(filters.stock_status)) {
          cleanParams.stock_status = filters.stock_status;
        }
      }
      if (filters.product_id !== undefined && filters.product_id !== null && !isNaN(filters.product_id)) {
        cleanParams.product_id = filters.product_id;
      }
      if (filters.warehouse_id !== undefined && filters.warehouse_id !== null && !isNaN(filters.warehouse_id)) {
        cleanParams.warehouse_id = filters.warehouse_id;
      }
      if (filters.page !== undefined && filters.page !== null && !isNaN(filters.page)) {
        cleanParams.page = filters.page;
      }
      if (filters.page_size !== undefined && filters.page_size !== null && !isNaN(filters.page_size)) {
        cleanParams.page_size = filters.page_size;
      }
      if (filters.ordering !== undefined && filters.ordering !== null && filters.ordering !== '') {
        cleanParams.ordering = filters.ordering;
      }
      if (filters.for_export !== undefined) {
        cleanParams.for_export = filters.for_export ? 'true' : 'false';
      }
      if (filters.only_kpis !== undefined) {
        cleanParams.only_kpis = filters.only_kpis ? 'true' : 'false';
      }
      if (filters.skip_table !== undefined) {
        cleanParams.only_kpis = filters.skip_table ? 'true' : 'false';
      }
      if (filters.skip_kpis !== undefined) {
        cleanParams.skip_kpis = filters.skip_kpis ? 'true' : 'false';
      }
      if (filters.sales_months !== undefined && filters.sales_months !== null && !isNaN(filters.sales_months)) {
        cleanParams.sales_months = filters.sales_months;
      }
      if (filters.action_category_key) {
        cleanParams.action_category_key = filters.action_category_key;
      }
      if (
        filters.recommendation_on_demand !== undefined &&
        filters.recommendation_on_demand !== null
      ) {
        cleanParams.recommendation_on_demand = filters.recommendation_on_demand ? 'true' : 'false';
      }
      appendIntIdListParam(cleanParams, 'supplier_id', filters.supplier_id);
      if (filters.positive_stock_only) {
        cleanParams.positive_stock_only = 'true';
      }
    }
    
    return fetchApi<PolicyCalculations>('inventory/policy-calculations/', {
      params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined
    });
  },

  getRecommendationChart: async (filters?: {
    family?: number | number[];
    stock_status?: number | number[];
    product_id?: number;
    warehouse_id?: number;
    organization_id?: number;
    recommendation_on_demand?: boolean;
    supplier_id?: number | number[];
    positive_stock_only?: boolean;
  } & ProductCatApiParams): Promise<RecommendationChartResponse> => {
    const cleanParams: ApiQueryParams = {};
    if (filters) {
      if (filters.organization_id !== undefined && !isNaN(filters.organization_id)) {
        cleanParams.organization_id = filters.organization_id;
      }
      appendIntIdListParam(cleanParams, 'family', filters.family);
      appendProductCatRequestParams(cleanParams, filters);
      if (filters.stock_status !== undefined) {
        if (Array.isArray(filters.stock_status)) {
          const arr = filters.stock_status.filter((n) => typeof n === 'number' && !Number.isNaN(n));
          if (arr.length > 0) cleanParams.stock_status = arr;
        } else if (!Number.isNaN(filters.stock_status)) {
          cleanParams.stock_status = filters.stock_status;
        }
      }
      if (filters.product_id !== undefined && !isNaN(filters.product_id)) {
        cleanParams.product_id = filters.product_id;
      }
      if (filters.warehouse_id !== undefined && !isNaN(filters.warehouse_id)) {
        cleanParams.warehouse_id = filters.warehouse_id;
      }
      if (
        filters.recommendation_on_demand !== undefined &&
        filters.recommendation_on_demand !== null
      ) {
        cleanParams.recommendation_on_demand = filters.recommendation_on_demand
          ? 'true'
          : 'false';
      }
      appendIntIdListParam(cleanParams, 'supplier_id', filters.supplier_id);
      if (filters.positive_stock_only) {
        cleanParams.positive_stock_only = 'true';
      }
    }
    return fetchApi<RecommendationChartResponse>('inventory/recommendation-chart/', {
      params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined,
    });
  },
  
  // Get inventory value history
  getInventoryValueHistory: async (months: number = 12): Promise<InventoryValueHistory[]> => {
    return fetchApi<InventoryValueHistory[]>('inventory/value-history/', {
      params: { months }
    });
  },
  
  // Get inventory simulation (now real data)
  getInventorySimulation: async (params?: {
    product_id?: number;
    days?: number;
  }): Promise<InventorySimulation[]> => {
    const cleanParams: Record<string, number> = {};
    
    if (params) {
      if (params.product_id !== undefined && params.product_id !== null && !isNaN(params.product_id)) {
        cleanParams.product_id = params.product_id;
      }
      if (params.days !== undefined && params.days !== null && !isNaN(params.days)) {
        cleanParams.days = params.days;
      }
    }
    
    return fetchApi<InventorySimulation[]>('inventory/simulation/', { 
      params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined 
    });
  },

  // Get inventory movement history (6 months historical data)
  getInventoryMovementHistory: async (params?: {
    product_id?: number;
    months?: number;
    family?: number | number[];
    stock_status?: number | number[];
    warehouse_id?: number;
    /** Debe coincidir con la organización del usuario autenticado (validación en backend). */
    organization_id?: number;
  } & ProductCatApiParams): Promise<InventoryMovementHistory[]> => {
    const cleanParams: ApiQueryParams = {};
    
    if (params) {
      if (params.organization_id !== undefined && params.organization_id !== null && !isNaN(params.organization_id)) {
        cleanParams.organization_id = params.organization_id;
      }
      if (params.product_id !== undefined && params.product_id !== null && !isNaN(params.product_id)) {
        cleanParams.product_id = params.product_id;
      }
      if (params.months !== undefined && params.months !== null && !isNaN(params.months)) {
        cleanParams.months = params.months;
      }
      appendIntIdListParam(cleanParams, 'family', params.family);
      appendProductCatRequestParams(cleanParams, params);
      if (params.stock_status !== undefined && params.stock_status !== null) {
        if (Array.isArray(params.stock_status)) {
          const arr = params.stock_status.filter(
            (n) => typeof n === 'number' && !Number.isNaN(n)
          );
          if (arr.length > 0) {
            cleanParams.stock_status = arr;
          }
        } else if (!Number.isNaN(params.stock_status)) {
          cleanParams.stock_status = params.stock_status;
        }
      }
      if (params.warehouse_id !== undefined && params.warehouse_id !== null && !isNaN(params.warehouse_id)) {
        cleanParams.warehouse_id = params.warehouse_id;
      }
    }
    
    return fetchApi<InventoryMovementHistory[]>('inventory/movement-history/', { 
      params: Object.keys(cleanParams).length > 0 ? cleanParams : undefined 
    });
  },
  
  // Update reorder points (uses current policy endpoint)
  updateReorderPoints: async (productId: number, data: UpdateReorderPointRequest, warehouseId?: number): Promise<any> => {
    const params = typeof warehouseId === 'number' ? { warehouse_id: warehouseId } : undefined;
    const result = await fetchApi<any>(`inventory/policies/product/${productId}/`, {
      method: 'PATCH',
      body: data,
      params
    });
    return result;
  },
  
  // Calculate optimal reorder points
  calculateOptimalReorderPoints: async (): Promise<any> => {
    return fetchApi<any>('inventory/calculate-optimal-reorder-points/', {
      method: 'POST',
    });
  },
  
  // Get inventory health status
  getInventoryHealthStatus: async (): Promise<InventoryHealthStatus[]> => {
    return fetchApi<InventoryHealthStatus[]>('inventory/health-status/');
  },

  // ==========================================
  // Nuevos métodos para gestión histórica de políticas
  // ==========================================

  // Get current policy for a product
  getCurrentPolicy: async (productId: number, warehouseId?: number): Promise<InventoryPolicy> => {
    const params = typeof warehouseId === 'number' ? { warehouse_id: warehouseId } : undefined;
    return fetchApi<InventoryPolicy>(`inventory/policies/product/${productId}/`, {
      params
    });
  },

  // Update current policy for a product
  updateCurrentPolicy: async (productId: number, data: Partial<InventoryPolicy>): Promise<InventoryPolicy> => {
    return fetchApi<InventoryPolicy>(`inventory/policies/product/${productId}/`, {
      method: 'PATCH',
      body: data
    });
  },

  // Get policy history for a product
  getPolicyHistory: async (productId: number): Promise<PolicyHistoryResponse> => {
    return fetchApi<PolicyHistoryResponse>(`inventory/policies/history/${productId}/`);
  },

  // Create new policy for a product
  createPolicy: async (data: CreatePolicyRequest): Promise<InventoryPolicy> => {
    return fetchApi<InventoryPolicy>('inventory/policies/', {
      method: 'POST',
      body: data
    });
  },

  // Get all policies (with optional filters)
  getAllPolicies: async (params?: {
    product_id?: number;
  }): Promise<InventoryPolicy[]> => {
    return fetchApi<InventoryPolicy[]>('inventory/policies/', { params });
  },

  // Deactivate a specific policy
  deactivatePolicy: async (policyId: number): Promise<InventoryPolicy> => {
    return fetchApi<InventoryPolicy>(`inventory/policies/${policyId}/`, {
      method: 'PATCH',
      body: { active: false }
    });
  },

  // Get policy for a specific product (simplified)
  getPolicyForProduct: async (productId: number): Promise<InventoryPolicy | null> => {
    try {
      const policies = await fetchApi<InventoryPolicy[]>('inventory/policies/', {
        params: {
          product_id: productId
        }
      });
      return policies.length > 0 ? policies[0] : null;
    } catch (error) {
      console.error('Error fetching policy for product:', error);
      return null;
    }
  },

  // Clone policy (simplified - just copy to new product)
  clonePolicyToProduct: async (
    sourceProductId: number, 
    targetProductId: number
  ): Promise<InventoryPolicy> => {
    // First get the source policy
    const sourcePolicy = await inventoryService.getPolicyForProduct(sourceProductId);
    
    if (!sourcePolicy) {
      throw new Error(`No policy found for product ${sourceProductId}`);
    }

    // Create new policy with same values
    const newPolicyData: CreatePolicyRequest = {
      product: targetProductId,
      reorder_point: sourcePolicy.reorder_point ?? 0, // Convertir null a 0
      reorder_point_suggested: sourcePolicy.reorder_point_suggested,
      safety_stock: sourcePolicy.safety_stock,
      safety_stock_suggested: sourcePolicy.safety_stock_suggested,
      lead_time_days: sourcePolicy.lead_time_days,
      service_level_target: sourcePolicy.service_level_target
    };

    return inventoryService.createPolicy(newPolicyData);
  },

  /** TASK 20: marcar línea de OC como recibida (Stocker; sticky received_is_manual). */
  markPurchaseOrderItemReceived: async (
    itemId: number,
    payload?: { received_date?: string; received_quantity?: number | null },
  ): Promise<{
    success: boolean;
    product_id?: number;
    product_ids?: number[];
    supplier_id?: number;
    header_received?: boolean;
  }> => {
    const body: Record<string, unknown> = {};
    if (payload?.received_date) body.received_date = payload.received_date;
    if (payload?.received_quantity != null) body.received_quantity = payload.received_quantity;
    return fetchApi(`inventory/purchase-order-items/${itemId}/mark-received/`, {
      method: 'POST',
      body,
    });
  },

  /** TASK 20: marcar pedido completo como recibido. */
  markPurchaseOrderReceived: async (
    orderId: number,
    payload?: { received_date?: string },
  ): Promise<{
    success: boolean;
    product_ids?: number[];
    supplier_id?: number;
    header_received?: boolean;
    header_cancelled?: boolean;
  }> => {
    const body: Record<string, unknown> = {};
    if (payload?.received_date) body.received_date = payload.received_date;
    return fetchApi(`inventory/purchase-orders/${orderId}/mark-received/`, {
      method: 'POST',
      body,
    });
  },

  /** SEG-REC-002: cancelar pedido (líneas abiertas → CANCELLED). */
  cancelPurchaseOrder: async (
    orderId: number,
  ): Promise<{
    success: boolean;
    product_ids?: number[];
    supplier_id?: number;
    header_cancelled?: boolean;
    header_status?: string;
  }> => {
    return fetchApi(`inventory/purchase-orders/${orderId}/cancel/`, {
      method: 'POST',
      body: {},
    });
  },

  /** SEG-REC-002 parcial: cancelar líneas seleccionadas. */
  cancelPurchaseOrderItems: async (
    itemIds: number[],
  ): Promise<{
    success: boolean;
    product_ids?: number[];
    supplier_ids?: number[];
    header_cancelled?: boolean;
  }> => {
    return fetchApi('inventory/purchase-order-items/cancel-bulk/', {
      method: 'POST',
      body: { item_ids: itemIds },
    });
  },

  /** TASK 20: marcar líneas seleccionadas como recibidas. */
  markPurchaseOrderItemsReceived: async (
    itemIds: number[],
    payload?: { received_date?: string; received_quantity?: number | null },
  ): Promise<{
    success: boolean;
    product_ids?: number[];
    supplier_ids?: number[];
  }> => {
    const body: Record<string, unknown> = { item_ids: itemIds };
    if (payload?.received_date) body.received_date = payload.received_date;
    if (payload?.received_quantity != null) body.received_quantity = payload.received_quantity;
    return fetchApi('inventory/purchase-order-items/mark-received-bulk/', {
      method: 'POST',
      body,
    });
  },
};